import copy from '../copy/en/intake.json' with { type: 'json' };
import { formatCopy } from '../copy/format.js';
import { OFFICE_DAYS, cloneSchedule, defaultBreak, defaultSchedule, emptySchedule, emptyTime, inferSharedHours, formatOfficeHours, fromMinutes, parseOfficeHours, scheduleIssue, timeMinutes, type OfficeDay, type OfficeSchedule, type OfficeTime, type OfficeTimeKey, type OfficeWeek } from './office-hours-model.js';

const DAYS_NAME = 'intakeProfile.operations.daysOpen';
const HOURS_NAME = 'intakeProfile.operations.typicalHours';
const element = <K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};
const timeLabels = () => ({ open: copy.officeHoursOpens, close: copy.officeHoursCloses, lunchStart: copy.officeHoursLunchStarts, lunchEnd: copy.officeHoursLunchEnds });

function createTime(day: string, key: OfficeTimeKey, changed: () => void, signal: AbortSignal) {
  const field = element('fieldset', 'office-time field'); field.dataset.time = key;
  const label = timeLabels()[key];
  field.append(element('legend', undefined, label));
  const segments = element('div', 'office-time-segments');
  const inputs = (['hour', 'minute'] as const).map(segment => {
    const input = element('input', `office-time-${segment}`);
    input.type = 'text'; input.inputMode = 'numeric'; input.maxLength = 2; input.autocomplete = 'off';
    input.placeholder = segment === 'hour' ? copy.officeHoursHourPlaceholder : copy.officeHoursMinutePlaceholder;
    input.dataset.segment = segment;
    input.setAttribute('aria-label', formatCopy(segment === 'hour' ? copy.officeHoursHourLabel : copy.officeHoursMinuteLabel, { day, time: label }));
    input.setAttribute('role', 'spinbutton'); input.setAttribute('aria-valuemin', segment === 'hour' ? '1' : '0'); input.setAttribute('aria-valuemax', segment === 'hour' ? '12' : '59');
    input.addEventListener('focus', () => input.select(), { signal });
    input.addEventListener('input', () => { if (!input.matches(':disabled')) { syncAria(); changed(); } }, { signal });
    input.addEventListener('keydown', event => {
      if (input.matches(':disabled') || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const step = event.key === 'ArrowUp' ? 1 : -1;
      const total = timeMinutes(get());
      if (total !== null) set(fromMinutes(total + step * (segment === 'hour' ? 60 : 1)));
      else {
        const min = segment === 'hour' ? 1 : 0; const range = segment === 'hour' ? 12 : 60;
        const current = Number(input.value);
        const valid = /^\d{1,2}$/.test(input.value) && Number.isInteger(current) && current >= min && current < min + range;
        const baseline = valid ? current : step > 0 ? min - 1 : min;
        input.value = String(((baseline - min + step + range) % range) + min);
        if (segment === 'minute') input.value = input.value.padStart(2, '0');
        syncAria();
      }
      changed();
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }, { signal });
    return input;
  });
  const [hour, minute] = inputs as [HTMLInputElement, HTMLInputElement];
  const period = element('button', 'office-time-period', 'AM'); period.type = 'button'; period.dataset.segment = 'period';
  period.addEventListener('click', () => {
    if (period.matches(':disabled')) return;
    period.textContent = period.textContent === 'AM' ? 'PM' : 'AM'; syncAria(); changed();
    period.dispatchEvent(new Event('input', { bubbles: true }));
  }, { signal });
  const get = (): OfficeTime => ({ hour: hour.value, minute: minute.value, period: period.textContent === 'PM' ? 'PM' : 'AM' });
  function syncAria(): void {
    for (const input of inputs) {
      const valid = /^\d{1,2}$/.test(input.value) && Number(input.value) >= Number(input.getAttribute('aria-valuemin')) && Number(input.value) <= Number(input.getAttribute('aria-valuemax'));
      if (valid) input.setAttribute('aria-valuenow', String(Number(input.value)));
      else input.removeAttribute('aria-valuenow');
    }
    period.setAttribute('aria-label', formatCopy(copy.officeHoursPeriodLabel, { day, time: label, period: period.textContent! }));
  }
  function set(value: OfficeTime): void { hour.value = value.hour; minute.value = value.minute; period.textContent = value.period; syncAria(); }
  segments.append(hour, element('span', 'office-time-colon', ':'), minute, period); field.append(segments);
  return { field, hour, minute, period, get, set };
}

const dayLabels = (): Record<OfficeDay, [string, string]> => ({
  Monday: [copy.officeHoursMonday, copy.officeHoursMon], Tuesday: [copy.officeHoursTuesday, copy.officeHoursTue],
  Wednesday: [copy.officeHoursWednesday, copy.officeHoursWed], Thursday: [copy.officeHoursThursday, copy.officeHoursThu],
  Friday: [copy.officeHoursFriday, copy.officeHoursFri], Saturday: [copy.officeHoursSaturday, copy.officeHoursSat],
  Sunday: [copy.officeHoursSunday, copy.officeHoursSun],
});
function icon(kind: 'coffee' | 'pencil' | 'remove'): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18');
  svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.7');
  svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', kind === 'coffee' ? 'M4 8h12v7a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8Zm12 1h2a3 3 0 0 1 0 6h-2M3 22h15M7 2v3m5-3v3'
    : kind === 'pencil' ? 'm4 16-1 5 5-1L20 8l-4-4L4 16Zm10-10 4 4' : 'm6 6 12 12M6 18 18 6');
  svg.append(path); return svg;
}
function createScheduleEditor(label: string, changed: () => void, signal: AbortSignal) {
  const elementNode = element('div', 'office-hours-editor');
  const main = element('div', 'office-hours-time-pair');
  const breakNode = element('div', 'office-hours-break');
  const breakPair = element('div', 'office-hours-time-pair');
  const actions = element('div', 'office-hours-actions');
  const controls = new Map<OfficeTimeKey, ReturnType<typeof createTime>>();
  let hasLunch = false; let enabled = true;
  for (const key of ['open', 'close', 'lunchStart', 'lunchEnd'] as const) {
    const control = createTime(label, key, changed, signal); controls.set(key, control);
    (key.startsWith('lunch') ? breakPair : main).append(control.field);
  }
  const add = element('button', 'office-hours-break-action'); add.type = 'button'; add.dataset.hoursAction = 'add-break';
  add.append(icon('coffee'), element('span', undefined, copy.officeHoursAddBreak));
  const remove = element('button', 'office-hours-break-remove'); remove.type = 'button'; remove.dataset.hoursAction = 'remove-break';
  remove.setAttribute('aria-label', formatCopy(copy.officeHoursRemoveBreak, { day: label })); remove.append(icon('remove'));
  breakNode.append(breakPair, remove); actions.append(add); elementNode.append(main, breakNode, actions);
  function display(active = enabled): void {
    enabled = active; breakNode.hidden = !hasLunch; add.hidden = hasLunch;
    add.disabled = !enabled; remove.disabled = !enabled || !hasLunch;
    for (const [key, control] of controls) control.field.disabled = !enabled || key.startsWith('lunch') && !hasLunch;
  }
  add.addEventListener('click', () => {
    if (add.matches(':disabled')) return;
    const current = { ...get(), lunch: { start: controls.get('lunchStart')!.get(), end: controls.get('lunchEnd')!.get() } };
    if (scheduleIssue('Monday', current)) {
      const lunch = defaultBreak(current);
      controls.get('lunchStart')!.set(lunch.start); controls.get('lunchEnd')!.set(lunch.end);
    }
    hasLunch = true; display(); changed();
    controls.get('lunchStart')!.hour.focus();
    add.dispatchEvent(new Event('input', { bubbles: true }));
  }, { signal });
  remove.addEventListener('click', () => {
    if (remove.matches(':disabled')) return;
    hasLunch = false; display(); changed(); add.focus();
    remove.dispatchEvent(new Event('input', { bubbles: true }));
  }, { signal });
  const get = (): OfficeSchedule => ({ open: controls.get('open')!.get(), close: controls.get('close')!.get(),
    ...(hasLunch ? { lunch: { start: controls.get('lunchStart')!.get(), end: controls.get('lunchEnd')!.get() } } : {}) });
  function set(value: OfficeSchedule): void {
    hasLunch = !!value.lunch;
    controls.get('open')!.set(value.open); controls.get('close')!.set(value.close);
    controls.get('lunchStart')!.set(value.lunch?.start ?? emptyTime()); controls.get('lunchEnd')!.set(value.lunch?.end ?? emptyTime());
    display();
  }
  return { element: elementNode, actions, controls, add, get, set, display };
}
function readableTime(time: OfficeTime): string {
  return timeMinutes(time) === null ? copy.officeHoursIncomplete : `${time.hour}:${time.minute.padStart(2, '0')} ${time.period}`;
}
function readableSchedule(value: OfficeSchedule): string {
  const hours = `${readableTime(value.open)}–${readableTime(value.close)}`;
  const pause = value.lunch ? `${copy.officeHoursBreak}: ${readableTime(value.lunch.start)}–${readableTime(value.lunch.end)}` : copy.officeHoursNoBreak;
  return `${hours} · ${pause}`;
}

/** Shared hours and explicit exceptions project into the existing per-day scalar. */
export function createOfficeHours(form: HTMLFormElement) {
  const controller = new AbortController(); const { signal } = controller;
  const card = element('fieldset', 'field-group office-hours'); card.dataset.officeHours = '';
  card.append(element('legend', undefined, copy.officeHours));
  const top = element('div', 'office-hours-top');
  const chips = element('div', 'office-hours-day-chips');
  const meta = element('div', 'office-hours-meta');
  const count = element('span', 'office-hours-count'); count.setAttribute('aria-live', 'polite');
  meta.append(count, element('span', 'office-hours-local', copy.officeHoursLocalTime)); top.append(chips, meta); card.append(top);
  const carrier = element('input'); carrier.type = 'hidden'; carrier.name = HOURS_NAME;
  const daysError = element('em', 'err'); daysError.dataset.err = DAYS_NAME;
  const hoursError = element('em', 'err'); hoursError.dataset.err = HOURS_NAME;
  const retained = element('div', 'office-hours-retained-summary');
  const retainedText = element('p');
  const replace = element('button', 'btn btn-ghost btn-small', copy.officeHoursSetWeekly); replace.type = 'button'; replace.dataset.hoursAction = 'set-weekly';
  retained.append(element('p', 'hint', copy.officeHoursRetained), retainedText, replace); retained.hidden = true; card.append(retained);
  const sharedNode = element('div', 'office-hours-shared'); sharedNode.dataset.day = 'shared';
  const scope = element('small', 'office-hours-shared-scope', copy.officeHoursSharedScope);
  const shared = createScheduleEditor(copy.officeHoursShared, () => { errorTarget = undefined; syncValue(); }, signal);
  sharedNode.append(shared.element, scope);
  const exceptions = element('details', 'office-hours-exceptions');
  const exceptionSummary = element('summary');
  const customCount = element('span', 'office-hours-custom-count');
  exceptionSummary.append(element('span', undefined, copy.officeHoursDifferentDays), customCount); exceptions.append(exceptionSummary);
  card.append(sharedNode, exceptions, daysError, hoursError, carrier);
  const rows = new Map<OfficeDay, ReturnType<typeof createRow>>();
  const custom = new Set<OfficeDay>();
  let active: OfficeDay | undefined;
  let legacy = false;
  let errorTarget: HTMLElement | undefined;
  let disposed = false;
  const openDays = (): OfficeDay[] => OFFICE_DAYS.filter(day => rows.get(day)!.check.checked);
  const effective = (day: OfficeDay): OfficeSchedule => custom.has(day) ? rows.get(day)!.editor.get() : shared.get();
  function currentWeek(): OfficeWeek { return Object.fromEntries(openDays().map(day => [day, effective(day)])); }
  function display(): void {
    const open = openDays();
    const countText = formatCopy(copy.officeHoursDayCount, { count: open.length });
    if (count.textContent !== countText) count.textContent = countText;
    const customTotal = open.filter(day => custom.has(day)).length;
    customCount.textContent = formatCopy(copy.officeHoursCustomCount, { count: customTotal });
    customCount.hidden = customTotal === 0;
    sharedNode.hidden = legacy; exceptions.hidden = legacy || !open.length;
    shared.display(!legacy && !!open.length);
    for (const [day, row] of rows) {
      const isCustom = custom.has(day); const editing = active === day && row.check.checked && !legacy;
      row.row.hidden = !row.check.checked || legacy; row.panel.hidden = !editing;
      row.edit.setAttribute('aria-expanded', String(editing));
      row.editText.textContent = isCustom ? copy.officeHoursEdit : copy.officeHoursCustomize;
      row.edit.setAttribute('aria-label', formatCopy(isCustom ? copy.officeHoursEditDay : copy.officeHoursCustomizeDay, { day: dayLabels()[day][0] }));
      row.badge.hidden = !isCustom; row.chip.dataset.custom = String(isCustom);
      row.check.setAttribute('aria-label', isCustom ? formatCopy(copy.officeHoursCustomDay, { day: dayLabels()[day][0] }) : dayLabels()[day][0]);
      if (!isCustom) row.editor.set(shared.get());
      row.editor.display(editing); row.useShared.hidden = !isCustom; row.useShared.disabled = !editing;
      row.summary.textContent = readableSchedule(effective(day));
    }
  }
  function syncValue(): void { if (!legacy) carrier.value = formatOfficeHours(currentWeek()); display(); }
  function notify(): void { syncValue(); carrier.dispatchEvent(new Event('input', { bubbles: true })); }
  function createRow(day: OfficeDay) {
    const chip = element('label', 'office-hours-day-chip');
    const check = element('input'); check.type = 'checkbox'; check.name = DAYS_NAME; check.value = day;
    check.setAttribute('aria-label', dayLabels()[day][0]);
    chip.append(check, element('span', 'office-hours-chip-label', dayLabels()[day][1])); chips.append(chip);
    const row = element('div', 'office-hours-exception-row'); row.dataset.day = day;
    const header = element('div', 'office-hours-exception-header');
    const name = element('span', 'office-hours-day-name', dayLabels()[day][0]);
    const badge = element('span', 'office-hours-custom-badge', copy.officeHoursCustom);
    const summary = element('span', 'office-hours-summary');
    const edit = element('button', 'office-hours-edit'); edit.type = 'button'; edit.dataset.hoursAction = 'edit';
    const editText = element('span'); edit.append(icon('pencil'), editText);
    const panel = element('div', 'office-hours-custom-editor'); panel.id = `office-hours-${day}-editor`; edit.setAttribute('aria-controls', panel.id);
    const editor = createScheduleEditor(dayLabels()[day][0], () => { custom.add(day); errorTarget = undefined; syncValue(); }, signal);
    const useShared = element('button', 'office-hours-use-shared', copy.officeHoursUseShared); useShared.type = 'button'; useShared.dataset.hoursAction = 'use-shared';
    editor.actions.append(useShared); panel.append(editor.element); name.append(badge); header.append(name, summary, edit); row.append(header, panel);
    editor.set(defaultSchedule());
    edit.addEventListener('click', () => {
      if (edit.matches(':disabled')) return;
      active = active === day ? undefined : day; display();
      if (active === day) editor.controls.get('open')!.hour.focus();
    }, { signal });
    check.addEventListener('change', () => {
      if (check.matches(':disabled')) return;
      errorTarget = undefined; if (!check.checked && active === day) active = undefined; notify();
    }, { signal });
    useShared.addEventListener('click', () => {
      if (useShared.matches(':disabled')) return;
      custom.delete(day); active = undefined; notify(); edit.focus();
    }, { signal });
    return { row, chip, check, header, summary, badge, edit, editText, panel, editor, useShared };
  }
  for (const day of OFFICE_DAYS) { const row = createRow(day); rows.set(day, row); exceptions.append(row.row); }
  function reset(): void {
    legacy = false; retained.hidden = true; errorTarget = undefined; active = undefined; custom.clear(); exceptions.open = false;
    shared.set(defaultSchedule());
    for (const [index, day] of OFFICE_DAYS.entries()) {
      const row = rows.get(day)!; row.check.checked = index < 5; row.check.defaultChecked = index < 5; row.editor.set(defaultSchedule());
    }
    syncValue();
  }
  function restore(open: unknown, rawHours: unknown): void {
    const raw = typeof rawHours === 'string' ? rawHours : '';
    const parsed = parseOfficeHours(raw); legacy = parsed === null;
    carrier.value = raw; retainedText.textContent = raw; retained.hidden = !legacy;
    errorTarget = undefined; active = undefined; custom.clear(); exceptions.open = false;
    const week: OfficeWeek = {};
    for (const day of OFFICE_DAYS) {
      const row = rows.get(day)!; row.check.checked = Array.isArray(open) && open.includes(day);
      const value = parsed?.[day] ?? emptySchedule(); row.editor.set(value);
      if (row.check.checked) week[day] = value;
    }
    const inferred = inferSharedHours(week); shared.set(inferred.shared);
    for (const day of inferred.custom) custom.add(day);
    // Keep the exact restored scalar until a deliberate edit changes effective hours.
    display();
  }
  replace.addEventListener('click', () => {
    if (replace.matches(':disabled')) return;
    legacy = false; retained.hidden = true; custom.clear(); active = undefined; shared.set(defaultSchedule());
    notify(); (openDays().length ? shared.controls.get('open')!.hour : rows.get('Monday')!.check).focus();
  }, { signal });
  function validate(): Record<string, string> {
    errorTarget = undefined;
    const open = openDays();
    if (!open.length) { errorTarget = rows.get('Monday')!.check; return { [DAYS_NAME]: copy.officeHoursChooseDay }; }
    if (legacy) return {};
    for (const day of open) {
      const issue = scheduleIssue(day, effective(day)); if (!issue) continue;
      if (custom.has(day)) { active = day; exceptions.open = true; display(); }
      const editor = custom.has(day) ? rows.get(day)!.editor : shared;
      errorTarget = editor.controls.get(issue.time)![issue.segment];
      const reason = { hour: copy.officeHoursInvalidHour, minute: copy.officeHoursInvalidMinute, order: copy.officeHoursInvalidOrder, lunch: copy.officeHoursInvalidLunch }[issue.reason];
      return { [HOURS_NAME]: formatCopy(copy.officeHoursError, { day: dayLabels()[day][0], time: timeLabels()[issue.time], reason }) };
    }
    return {};
  }
  function errorControl(name: string): HTMLElement | undefined {
    if (name === DAYS_NAME) return rows.get('Monday')!.check;
    if (name !== HOURS_NAME) return undefined;
    if (errorTarget?.isConnected) return errorTarget;
    return legacy ? replace : openDays().length ? shared.controls.get('open')!.hour : rows.get('Monday')!.check;
  }
  form.addEventListener('reset', () => { queueMicrotask(() => { if (!disposed) reset(); }); }, { signal });
  reset();
  return { element: card, restore, validate, errorControl, dispose: () => { disposed = true; controller.abort(); } };
}

/** Readable review keeps draft-only incomplete tokens out of the interface. */
export function officeHoursReview(raw: string): string {
  const parsed = parseOfficeHours(raw);
  if (!parsed) return raw;
  const show = readableTime;
  return OFFICE_DAYS.filter(day => parsed[day]).map(day => {
    const value = parsed[day]!;
    return `${dayLabels()[day][0]} ${show(value.open)}–${show(value.close)}${value.lunch ? ` (${copy.officeHoursLunch}: ${show(value.lunch.start)}–${show(value.lunch.end)})` : ''}`;
  }).join('; ');
}
